import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
    FileText, Trash2, Upload, FileUp,
    ExternalLink, ChevronLeft,
    Calendar, Search, X, Check, User,
    FileSearch, AlertCircle, LayoutList, Eye as EyeIcon,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PathologyServices from '../../shared/ApiServices/PathologyServices';
import {
    extractPathologyPatientName,
    extractPathologyReportHtml,
    extractPathologyReportId,
    extractPathologyReportUrl,
    extractPathologyStudiesList,
    extractPathologyStudyId,
    PathologyReport,
    PathologyStudyItem,
} from './pathologyUtils';
import {
    loadReportPreviewFromUrl,
    ReportPreviewContent,
    revokeReportPreviewBlob,
} from './reportPreviewUtils';
import { PathologyReportsSkeleton } from '../../shared/custom-ui/skeleton/Pathology';
import { usePathologyPermissions } from './usePathologyPermissions';
import { useMediaQuery } from '../../hooks/useMediaQuery';

const STUDIES_LIMIT = 50;
const REPORT_ACCEPT = '.pdf,.doc,.docx,.txt,.json,.xml,.html,.htm';

// ── Formatters ────────────────────────────────────────────────────────────────

const formatPatientInfo = (info: any): string => {
    if (!info) return '—';
    if (typeof info === 'string') return info;
    try { return JSON.stringify(info, null, 2); } catch { return String(info); }
};

const fmtStudyDate = (raw: string): string => {
    if (!raw) return '—';
    if (/^\d{8}$/.test(raw)) {
        const d = new Date(`${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}T00:00:00`);
        if (!isNaN(d.getTime()))
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    const d = new Date(raw);
    return !isNaN(d.getTime())
        ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : raw;
};

const fmtReportDate = (report: PathologyReport): string => {
    const raw = String(report.date ?? report.createdAt ?? report.updatedAt ?? '');
    if (!raw) return '—';
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw.slice(0, 19).replace('T', ' ');
    return (
        d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
        ' · ' +
        d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
    );
};

const cleanPatientName = (raw: string): string => {
    if (!raw || raw === 'Unknown') return 'Unknown Patient';
    let n = raw.replace(/\.[a-z]{2,5}$/i, '').replace(/__+/g, ' ').replace(/_/g, ' ');
    n = n
        .replace(/\s+\d{1,3}\s*YRS?\s*[MF]?\s*/gi, ' ')
        .replace(/\s+\d+\s*(?:MR|CR|DR|XR)\s*\d+/gi, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim();
    return n.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// ── File-type metadata ─────────────────────────────────────────────────────────

interface FileTypeMeta { label: string; color: string; bg: string }

const FILE_TYPE: Record<string, FileTypeMeta> = {
    pdf: { label: 'PDF', color: '#DC2626', bg: '#FEF2F2' },
    docx: { label: 'DOCX', color: '#2563EB', bg: '#EFF6FF' },
    doc: { label: 'DOC', color: '#2563EB', bg: '#EFF6FF' },
    html: { label: 'HTML', color: '#D97706', bg: '#FFFBEB' },
    htm: { label: 'HTML', color: '#D97706', bg: '#FFFBEB' },
    txt: { label: 'TXT', color: '#6B7280', bg: '#F3F4F6' },
    json: { label: 'JSON', color: '#7C3AED', bg: '#F5F3FF' },
    xml: { label: 'XML', color: '#059669', bg: '#ECFDF5' },
};

const getFileExt = (report: PathologyReport): string => {
    const name = String(report.fileName ?? report.file_name ?? '');
    if (name.includes('.')) return (name.split('.').pop() ?? '').toLowerCase();
    const url = extractPathologyReportUrl(report);
    try { return (new URL(url).pathname.split('.').pop() ?? '').toLowerCase(); } catch { return ''; }
};

const getFileTypeMeta = (report: PathologyReport): FileTypeMeta => {
    const ext = getFileExt(report);
    return FILE_TYPE[ext] ?? { label: ext.toUpperCase() || 'FILE', color: '#6B7280', bg: '#F3F4F6' };
};

const PALETTE = ['#045EB8', '#7C3AED', '#059669', '#D97706', '#DC2626', '#0891B2', '#EA580C'];
const pickStudyColor = (id: string) => PALETTE[id.charCodeAt(0) % PALETTE.length];

// ── Study Dropdown ─────────────────────────────────────────────────────────────

interface StudyDropdownProps {
    studies: PathologyStudyItem[];
    selectedStudyId: string;
    loading: boolean;
    onChange: (id: string) => void;
}

const StudyDropdown: React.FC<StudyDropdownProps> = ({ studies, selectedStudyId, loading, onChange }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false); setQuery('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
        if (open) setTimeout(() => searchRef.current?.focus(), 50);
    }, [open]);

    const selected = useMemo(
        () => studies.find(s => extractPathologyStudyId(s) === selectedStudyId) ?? null,
        [studies, selectedStudyId]
    );

    const filtered = useMemo(() => {
        if (!query.trim()) return studies;
        const q = query.toLowerCase();
        return studies.filter(s => {
            const name = cleanPatientName(extractPathologyPatientName(s)).toLowerCase();
            const acc = String(s.AccessionNumber ?? s.accessionNumber ?? '').toLowerCase();
            return name.includes(q) || acc.includes(q);
        });
    }, [studies, query]);

    const handleSelect = (id: string) => { onChange(id); setOpen(false); setQuery(''); };

    if (loading) {
        return (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-[var(--v2-border-interactive)] bg-[var(--v2-bg-surface)]">
                <div className="w-6 h-6 rounded-md bg-[var(--v2-border-subtle)] flex-shrink-0" />
                <div className="flex flex-col gap-1 flex-1 min-w-0">
                    <div className="h-2.5 w-28 rounded bg-[var(--v2-border-subtle)]" />
                    <div className="h-2 w-16 rounded bg-[var(--v2-border-subtle)]" />
                </div>
            </div>
        );
    }

    const selName = selected ? cleanPatientName(extractPathologyPatientName(selected)) : '';
    const selAcc = selected ? String(selected.AccessionNumber ?? selected.accessionNumber ?? '') : '';
    const selDate = selected ? fmtStudyDate(selected.StudyDate ?? selected.studyDate ?? '') : '';
    const selColor = selected ? pickStudyColor(extractPathologyStudyId(selected)) : '#6B7280';

    return (
        <div ref={containerRef} className="relative" id="study-dropdown-root">
            <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen(o => !o)}
                disabled={!studies.length}
                className={[
                    'flex items-center gap-2.5 px-3 py-2 rounded-xl border text-left w-full',
                    'cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150',
                    open
                        ? 'border-[var(--v2-primary-500)] shadow-[0_0_0_3px_rgba(4,94,184,0.12)] bg-[var(--v2-bg-surface)]'
                        : 'border-[var(--v2-border-interactive)] bg-[var(--v2-bg-surface)] hover:border-[var(--v2-primary-400)]',
                ].join(' ')}
            >
                {selected ? (
                    <div
                        className="w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center text-[10px] font-extrabold text-white select-none"
                        style={{ background: selColor }}
                    >
                        {getInitials(selName)}
                    </div>
                ) : (
                    <div className="w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center bg-[var(--v2-bg-surface)] border border-[var(--v2-border-card)]">
                        <User className="w-3.5 h-3.5 text-[var(--v2-text-muted)]" strokeWidth={1.8} />
                    </div>
                )}
                <div className="flex flex-col flex-1 min-w-0">
                    {selected ? (
                        <>
                            <span className="text-[12px] font-bold text-[var(--v2-text-heading)] truncate leading-snug">{selName}</span>
                            <div className="flex items-center gap-2 mt-0.5">
                                {selAcc && <span className="text-[10px] font-mono text-[var(--v2-text-muted)]">#{selAcc}</span>}
                                {selDate && selDate !== '—' && <span className="text-[10px] text-[var(--v2-text-muted)]">{selDate}</span>}
                            </div>
                        </>
                    ) : (
                        <span className="text-[12px] text-[var(--v2-text-muted)]">
                            {studies.length ? 'Select a study…' : 'No studies available'}
                        </span>
                    )}
                </div>
                <div className={`w-4 h-4 text-[var(--v2-text-muted)] flex-shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}>
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
            </button>

            {open && studies.length > 0 && (
                <div
                    role="listbox"
                    className="absolute top-full left-0 right-0 mt-1.5 rounded-2xl border border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card)] z-50 overflow-hidden"
                    style={{ maxHeight: 340, boxShadow: 'var(--v2-shadow-premium)' }}
                >
                    {studies.length > 4 && (
                        <div className="px-3 pt-3 pb-2 border-b border-[var(--v2-border-subtle)]">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--v2-text-muted)] pointer-events-none" />
                                <input
                                    ref={searchRef}
                                    type="text"
                                    placeholder="Search patient or accession…"
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    className="w-full pl-8 pr-7 py-1.5 rounded-lg text-xs outline-none border border-[var(--v2-border-interactive)] bg-[var(--v2-bg-surface)] text-[var(--v2-text-primary)] placeholder:text-[var(--v2-text-muted)]"
                                />
                                {query && (
                                    <button type="button" onClick={() => setQuery('')}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--v2-text-muted)] hover:text-[var(--v2-text-primary)] cursor-pointer bg-transparent border-0 p-0">
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="overflow-y-auto" style={{ maxHeight: studies.length > 4 ? 272 : 340 }}>
                        {filtered.length === 0 ? (
                            <div className="flex flex-col items-center py-8 gap-2 text-center px-4">
                                <FileSearch className="w-5 h-5 text-[var(--v2-text-muted)]" strokeWidth={1.5} />
                                <p className="text-xs text-[var(--v2-text-muted)] m-0">No studies match "{query}"</p>
                            </div>
                        ) : (
                            filtered.map((study, idx) => {
                                const id = extractPathologyStudyId(study);
                                const name = cleanPatientName(extractPathologyPatientName(study));
                                const acc = String(study.AccessionNumber ?? study.accessionNumber ?? '');
                                const date = fmtStudyDate(study.StudyDate ?? study.studyDate ?? '');
                                const color = pickStudyColor(id);
                                const isSel = id === selectedStudyId;
                                return (
                                    <div
                                        key={id || idx}
                                        role="option"
                                        aria-selected={isSel}
                                        onClick={() => handleSelect(id)}
                                        className={[
                                            'flex items-center gap-3 px-3.5 py-2.5 cursor-pointer transition-colors border-b border-[var(--v2-border-divider)] last:border-0',
                                            isSel ? 'bg-[var(--v2-primary-50,rgba(4,94,184,0.06))]' : 'hover:bg-[var(--v2-bg-hover,rgba(0,0,0,0.03))]',
                                        ].join(' ')}
                                    >
                                        <div
                                            className="w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center text-[10px] font-extrabold text-white select-none"
                                            style={{ background: color }}
                                        >
                                            {getInitials(name)}
                                        </div>
                                        <div className="flex flex-col flex-1 min-w-0">
                                            <span className="text-[12px] font-bold text-[var(--v2-text-heading)] truncate leading-snug">{name}</span>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                {acc && <span className="text-[10px] font-mono text-[var(--v2-text-muted)]">#{acc}</span>}
                                                {date && date !== '—' && <span className="text-[10px] text-[var(--v2-text-muted)]">{date}</span>}
                                            </div>
                                        </div>
                                        {isSel && <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--v2-primary-500)' }} strokeWidth={2.5} />}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Delete Confirmation Modal ─────────────────────────────────────────────────

const DeleteConfirmModal: React.FC<{
    reportName: string;
    isDeleting: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}> = ({ reportName, isDeleting, onCancel, onConfirm }) => {
    return createPortal(
        <div
            className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)' }}
            onClick={e => { if (e.target === e.currentTarget) onCancel(); }}
        >
            <div className="w-full max-w-md rounded-2xl border border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card)] overflow-hidden" style={{ boxShadow: 'var(--v2-shadow-modal)' }}>
                <div className="h-[3px] bg-red-500" />
                <div className="px-6 py-5">
                    <div className="flex items-start gap-4 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <AlertCircle className="w-5 h-5 text-red-500" strokeWidth={2} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-[15px] font-bold text-[var(--v2-text-heading)] m-0 leading-snug">Delete this report?</h3>
                            <p className="text-[12px] text-[var(--v2-text-muted)] m-0 mt-1.5 leading-relaxed">
                                This action cannot be undone. The file will be permanently removed.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-surface)] mb-5">
                        <FileText className="w-4 h-4 text-[var(--v2-text-muted)] flex-shrink-0" strokeWidth={1.8} />
                        <span className="text-[12px] font-semibold text-[var(--v2-text-secondary)] truncate">
                            {reportName || 'Pathology Report'}
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={isDeleting}
                            className="flex-1 py-2.5 rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-surface)] text-sm font-bold text-[var(--v2-text-secondary)] cursor-pointer hover:text-[var(--v2-text-primary)] disabled:opacity-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={onConfirm}
                            disabled={isDeleting}
                            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white cursor-pointer disabled:opacity-60 hover:opacity-90 active:scale-[0.98] transition-all"
                            style={{ background: '#DC2626' }}
                        >
                            {isDeleting ? 'Deleting…' : 'Delete Report'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

// ── Main component ─────────────────────────────────────────────────────────────

export const Reports: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const urlStudyId = searchParams.get('studyId') ?? '';

    const { canUploadReport, canDeleteReport } = usePathologyPermissions();
    const isDesktop = useMediaQuery('(min-width: 1024px)');

    const fileInputRef = useRef<HTMLInputElement>(null);
    const updateFileInputRef = useRef<HTMLInputElement>(null);

    // State
    const [studies, setStudies] = useState<PathologyStudyItem[]>([]);
    const [studiesLoading, setStudiesLoading] = useState(false);
    const [selectedStudyId, setSelectedStudyId] = useState(urlStudyId);
    const [reports, setReports] = useState<PathologyReport[]>([]);
    const [selectedReportId, setSelectedReportId] = useState('');
    const [reportPreview, setReportPreview] = useState<ReportPreviewContent | null>(null);
    const [reportPreviewLoading, setReportPreviewLoading] = useState(false);
    const reportPreviewRef = useRef<ReportPreviewContent | null>(null);
    const [reportLoading, setReportLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [deletingId, setDeletingId] = useState('');
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
    const [mobileTab, setMobileTab] = useState<'list' | 'preview'>('list');

    // Data fetching
    const loadStudies = useCallback(async () => {
        setStudiesLoading(true);
        try {
            const res = await PathologyServices.getPathologyStudies({ limit: STUDIES_LIMIT });
            const list = extractPathologyStudiesList(res);
            setStudies(list);
            setSelectedStudyId(prev => prev || (list.length ? extractPathologyStudyId(list[0]) : ''));
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to load studies');
        } finally { setStudiesLoading(false); }
    }, []);

    const loadReports = useCallback(async (studyId: string, preferredReportId?: string) => {
        if (!studyId) { setReports([]); setSelectedReportId(''); return; }
        setReportLoading(true);
        try {
            const response = await PathologyServices.getPathologyReport(studyId);
            const list: PathologyReport[] = Array.isArray(response)
                ? response
                : (response?.reports || response?.data || []);
            setReports(list);
            const nextId = (
                preferredReportId && list.some(r => extractPathologyReportId(r) === preferredReportId)
                    ? preferredReportId
                    : list[0] ? extractPathologyReportId(list[0]) : ''
            );
            setSelectedReportId(nextId);
        } catch (err: any) {
            setReports([]); setSelectedReportId('');
            toast.error(err?.response?.data?.message || err?.message || 'Failed to load reports');
        } finally { setReportLoading(false); }
    }, []);

    const handleSelectReport = useCallback(async (reportId: string) => {
        if (!reportId || !selectedStudyId) return;
        setSelectedReportId(reportId);
        setMobileTab('preview');
        setReportLoading(true);
        try {
            const response: PathologyReport = await PathologyServices.getPathologyReport(selectedStudyId, reportId);
            setReports(prev => prev.map(r =>
                extractPathologyReportId(r) === reportId ? { ...r, ...response } : r
            ));
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to load report');
        } finally { setReportLoading(false); }
    }, [selectedStudyId]);

    useEffect(() => { loadStudies(); }, [loadStudies]);
    useEffect(() => { if (selectedStudyId) loadReports(selectedStudyId); }, [selectedStudyId, loadReports]);
    useEffect(() => {
        if (urlStudyId && studies.length > 0) setSelectedStudyId(urlStudyId);
    }, [urlStudyId, studies.length]);

    // Derived
    const report = useMemo(() => {
        if (!reports.length) return null;
        if (selectedReportId) return reports.find(r => extractPathologyReportId(r) === selectedReportId) ?? reports[0];
        return reports[0];
    }, [reports, selectedReportId]);

    const selectedStudy = useMemo(
        () => studies.find(s => extractPathologyStudyId(s) === selectedStudyId) ?? null,
        [studies, selectedStudyId]
    );

    const reportId = useMemo(() => (report ? extractPathologyReportId(report) : ''), [report]);
    const reportUrl = useMemo(() => (report ? extractPathologyReportUrl(report) : ''), [report]);
    const reportFileName = useMemo(() => String(report?.fileName ?? report?.file_name ?? ''), [report]);

    // Preview
    useEffect(() => { reportPreviewRef.current = reportPreview; }, [reportPreview]);
    useEffect(() => { return () => revokeReportPreviewBlob(reportPreviewRef.current); }, []);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            revokeReportPreviewBlob(reportPreviewRef.current);
            setReportPreview(null);
            if (!report) { setReportPreviewLoading(false); return; }
            const inlineHtml = extractPathologyReportHtml(report);
            if (inlineHtml.trim()) {
                setReportPreview({ kind: 'html', html: inlineHtml });
                setReportPreviewLoading(false); return;
            }
            const url = extractPathologyReportUrl(report);
            if (!url) { setReportPreviewLoading(false); return; }
            setReportPreviewLoading(true);
            try {
                const preview = await loadReportPreviewFromUrl(url);
                if (cancelled) { revokeReportPreviewBlob(preview); return; }
                setReportPreview(preview);
            } catch {
                if (!cancelled) setReportPreview(null);
            } finally {
                if (!cancelled) setReportPreviewLoading(false);
            }
        };
        run();
        return () => { cancelled = true; };
    }, [report]);

    // Open
    const openReportFile = useCallback(async () => {
        if (!reportUrl) return;
        if (reportPreview?.kind === 'pdf' && reportPreview?.blobUrl) {
            window.open(reportPreview.blobUrl, '_blank', 'noopener,noreferrer'); return;
        }
        if ((reportPreview?.kind === 'html' || reportPreview?.kind === 'docx') && reportPreview?.html) {
            const blob = new Blob([reportPreview.html], { type: 'text/html' });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank', 'noopener,noreferrer');
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000); return;
        }
        if (reportPreview?.kind === 'text' && reportPreview?.text) {
            const blob = new Blob([reportPreview.text], { type: 'text/plain' });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank', 'noopener,noreferrer');
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000); return;
        }
        try {
            const res = await fetch(reportUrl);
            const buffer = await res.arrayBuffer();
            const mime = res.headers.get('content-type') ?? 'application/octet-stream';
            const blob = new Blob([buffer], { type: mime });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank', 'noopener,noreferrer');
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
        } catch { window.open(reportUrl, '_blank', 'noopener,noreferrer'); }
    }, [reportUrl, reportPreview]);

    const openByUrl = useCallback((url: string) => {
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
    }, []);

    // Upload / Delete
    const handleUploadReport = async (file: File, isUpdate: boolean) => {
        if (!selectedStudyId) { toast.error('Select a study first'); return; }
        setUploading(true); setUploadProgress(0);
        try {
            const presigned = isUpdate && reportId
                ? await PathologyServices.updatePathologyReportPresignedUrl(selectedStudyId, reportId)
                : await PathologyServices.getPathologyReportPresignedUrl(selectedStudyId);
            await PathologyServices.uploadFileToPresignedUrl(presigned, file, setUploadProgress);
            toast.success(isUpdate ? 'Report updated' : 'Report uploaded');
            await loadReports(selectedStudyId);
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || 'Upload failed');
        } finally { setUploading(false); setUploadProgress(0); }
    };

    const onNewReportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]; e.target.value = '';
        if (file) handleUploadReport(file, false);
    };

    const onUpdateReportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]; e.target.value = '';
        if (file) handleUploadReport(file, true);
    };

    const handleDeleteReport = async () => {
        if (!deleteTarget || !selectedStudyId) return;
        const rId = deleteTarget.id;
        setDeletingId(rId);
        try {
            await PathologyServices.deletePathologyReport(selectedStudyId, rId);
            toast.success('Report deleted');
            setDeleteTarget(null);
            if (selectedReportId === rId) setSelectedReportId('');
            await loadReports(selectedStudyId);
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || 'Delete failed');
        } finally { setDeletingId(''); }
    };

    const triggerUpload = () => {
        if (!selectedStudyId) { toast.info('Select a study first'); return; }
        fileInputRef.current?.click();
    };

    const hasUrlCtx = !!urlStudyId;

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="v2-animate-fade-in flex flex-col gap-3">

            {/* Hidden inputs */}
            <input ref={fileInputRef} type="file" accept={REPORT_ACCEPT} className="hidden" onChange={onNewReportFile} />
            <input ref={updateFileInputRef} type="file" accept={REPORT_ACCEPT} className="hidden" onChange={onUpdateReportFile} />

            {/* Delete modal */}
            {deleteTarget && (
                <DeleteConfirmModal
                    reportName={deleteTarget.name}
                    isDeleting={!!deletingId}
                    onCancel={() => setDeleteTarget(null)}
                    onConfirm={handleDeleteReport}
                />
            )}

            {/*
            ╔══════════════════════════════════════════════════════════════╗
            ║  CONTEXTUAL STRIP                                            ║
            ║  Replaces the old "TOP BAR card".                            ║
            ║  No border · No shadow · No background card.                 ║
            ║  Reads as a flat continuation below the shell nav bar.       ║
            ╚══════════════════════════════════════════════════════════════╝
            */}
            <div className="flex items-center gap-3 flex-wrap" style={{ minHeight: 40 }}>

                {/* ← Back breadcrumb — URL context only */}
                {hasUrlCtx && (
                    <button
                        type="button"
                        onClick={() => navigate('/pathology/slides')}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-[var(--v2-text-muted)] hover:text-[var(--v2-text-primary)] cursor-pointer bg-transparent border-0 p-0 flex-shrink-0 transition-colors whitespace-nowrap"
                    >
                        <ChevronLeft className="w-3 h-3" strokeWidth={2.5} />
                        Slide Library
                    </button>
                )}

                {/* Patient context (URL mode) OR study dropdown (standalone) */}
                {hasUrlCtx ? (
                    <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                        {selectedStudy ? (
                            <>
                                <div className="w-px h-3.5 bg-[var(--v2-border-subtle)] flex-shrink-0" />
                                <div
                                    className="w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center text-[10px] font-extrabold text-white select-none"
                                    style={{ background: pickStudyColor(selectedStudyId) }}
                                >
                                    {getInitials(cleanPatientName(extractPathologyPatientName(selectedStudy)))}
                                </div>
                                <span className="text-[13px] font-bold text-[var(--v2-text-heading)] truncate">
                                    {cleanPatientName(extractPathologyPatientName(selectedStudy))}
                                </span>
                                {(selectedStudy.AccessionNumber || selectedStudy.accessionNumber) && (
                                    <span className="inline-flex items-center text-[10px] font-mono font-semibold text-[var(--v2-text-muted)] px-1.5 py-0.5 rounded-md bg-[var(--v2-bg-surface)] border border-[var(--v2-border-subtle)]">
                                        #{selectedStudy.AccessionNumber ?? selectedStudy.accessionNumber}
                                    </span>
                                )}
                                {(selectedStudy.StudyDate || selectedStudy.studyDate) && (
                                    <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-semibold text-[var(--v2-text-muted)]">
                                        <Calendar className="w-2.5 h-2.5" strokeWidth={2} />
                                        {fmtStudyDate(selectedStudy.StudyDate ?? selectedStudy.studyDate ?? '')}
                                    </span>
                                )}
                            </>
                        ) : studiesLoading ? (
                            <div className="h-4 w-36 rounded bg-[var(--v2-border-subtle)]" />
                        ) : (
                            <span className="text-xs text-[var(--v2-text-muted)] font-mono">{urlStudyId.slice(0, 14)}…</span>
                        )}
                    </div>
                ) : (
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)] flex-shrink-0 whitespace-nowrap">
                            Study
                        </span>
                        <div className="flex-1 min-w-0 max-w-sm">
                            <StudyDropdown
                                studies={studies}
                                selectedStudyId={selectedStudyId}
                                loading={studiesLoading}
                                onChange={setSelectedStudyId}
                            />
                        </div>
                    </div>
                )}

                {/* Upload Report — one button, right-side, primary */}
                {canUploadReport && selectedStudyId && (
                    <button
                        type="button"
                        disabled={uploading}
                        onClick={triggerUpload}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[12px] font-bold cursor-pointer disabled:opacity-50 transition-opacity whitespace-nowrap flex-shrink-0 ml-auto"
                        style={{ background: 'var(--v2-primary-500)', color: '#fff' }}
                    >
                        <Upload className="w-3.5 h-3.5" strokeWidth={2} />
                        <span className="hidden sm:inline">Upload Report</span>
                        <span className="sm:hidden">Upload</span>
                    </button>
                )}
            </div>

            {/* Upload progress bar */}
            {uploading && (
                <div className="rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] px-4 py-2.5">
                    <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="font-semibold text-[var(--v2-text-secondary)]">Uploading…</span>
                        <span className="font-black tabular-nums" style={{ color: 'var(--v2-primary-500)' }}>{uploadProgress}%</span>
                    </div>
                    <div className="h-1 rounded-full bg-[var(--v2-bg-surface)] overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-200" style={{ width: `${uploadProgress}%`, background: 'var(--v2-primary-500)' }} />
                    </div>
                </div>
            )}

            {/* Skeleton while first load */}
            {reportLoading && !reports.length && <PathologyReportsSkeleton />}

            {/* ══ 2-PANEL LAYOUT ══ */}
            {(reports.length > 0 || !reportLoading) && (
                <>
                    {/* Mobile tab switcher — below lg only */}
                    {reports.length > 0 && (
                        <div className="flex lg:hidden rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-surface)] p-1 gap-1">
                            {([
                                { id: 'list' as const, label: 'Reports', Icon: LayoutList, badge: reports.length },
                                { id: 'preview' as const, label: 'Preview', Icon: EyeIcon, badge: 0 },
                            ]).map(tab => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => setMobileTab(tab.id)}
                                    className={[
                                        'flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold cursor-pointer transition-all',
                                        mobileTab === tab.id
                                            ? 'bg-[var(--v2-bg-card)] text-[var(--v2-text-heading)] shadow-sm border border-[var(--v2-border-card)]'
                                            : 'text-[var(--v2-text-muted)]',
                                    ].join(' ')}
                                >
                                    <tab.Icon className="w-3.5 h-3.5" strokeWidth={2} />
                                    {tab.label}
                                    {tab.badge > 0 && (
                                        <span
                                            className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[9px] font-black"
                                            style={{ background: 'var(--v2-primary-100,rgba(4,94,184,0.1))', color: 'var(--v2-primary-600)' }}
                                        >
                                            {tab.badge}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}

                    <div
                        className="flex flex-col lg:flex-row gap-4 lg:items-stretch items-start"
                        style={isDesktop ? { height: 'calc(100vh - 250px)', minHeight: 480 } : undefined}
                    >
                        <div
                            className={[
                                'w-full lg:w-72 xl:w-80 flex-shrink-0 rounded-[var(--v2-radius-lg)] border border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card)] overflow-hidden flex flex-col',
                                reports.length > 0
                                    ? (mobileTab === 'list' ? 'flex flex-col' : 'hidden lg:flex lg:flex-col')
                                    : 'flex flex-col',
                            ].join(' ')}
                            style={{ minHeight: 320, boxShadow: 'var(--v2-shadow-card)' }}
                        >
                            {/* Panel label */}
                            <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--v2-border-subtle)]">
                                <FileText className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={2} style={{ color: 'var(--v2-primary-500)' }} />
                                <span className="text-[11px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)] flex-1">Reports</span>
                                {reports.length > 0 && (
                                    <span
                                        className="text-[9px] font-black px-1.5 py-0.5 rounded-full"
                                        style={{ background: 'var(--v2-primary-100,rgba(4,94,184,0.1))', color: 'var(--v2-primary-600)' }}
                                    >
                                        {reports.length}
                                    </span>
                                )}
                            </div>

                            {!reports.length ? (
                                <div className="flex flex-col items-center py-14 px-6 gap-4 text-center">
                                    <div
                                        className="w-12 h-12 rounded-2xl flex items-center justify-center"
                                        style={{ background: 'var(--v2-bg-surface)', border: '1.5px dashed var(--v2-border-card)' }}
                                    >
                                        <FileText className="w-5 h-5 text-[var(--v2-text-muted)]" strokeWidth={1.2} />
                                    </div>
                                    <div>
                                        <p className="text-[13px] font-bold text-[var(--v2-text-secondary)] m-0">No reports yet</p>
                                        <p className="text-[11px] text-[var(--v2-text-muted)] m-0 mt-1.5 max-w-[180px] leading-relaxed">
                                            {selectedStudyId
                                                ? 'Use "Upload Report" above to attach a file.'
                                                : 'Select a study above to view reports.'}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="overflow-y-auto v2-scrollbar flex-1 min-h-0">
                                    {reports.map((rpt, idx) => {
                                        const rId = extractPathologyReportId(rpt);
                                        const rUrl = extractPathologyReportUrl(rpt);
                                        const rName = String(rpt.fileName ?? rpt.file_name ?? '');
                                        const rDate = fmtReportDate(rpt);
                                        const meta = getFileTypeMeta(rpt);
                                        const isSel = selectedReportId === rId || (!selectedReportId && idx === 0);

                                        return (
                                            <div
                                                key={rId || idx}
                                                onClick={() => handleSelectReport(rId)}
                                                role="option"
                                                aria-selected={isSel}
                                                className="relative group px-4 py-3.5 border-b border-[var(--v2-border-divider)] last:border-0 cursor-pointer transition-colors duration-100"
                                                style={{ background: isSel ? 'var(--v2-primary-50,rgba(4,94,184,0.05))' : undefined }}
                                            >
                                                {/* Left selection bar */}
                                                <div
                                                    className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r transition-all duration-150"
                                                    style={{ background: isSel ? 'var(--v2-primary-500)' : 'transparent' }}
                                                />

                                                {/* Badge + version + date */}
                                                <div className="flex items-center gap-2 mb-1.5">
                                                    <span
                                                        className="inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider flex-shrink-0"
                                                        style={{ background: meta.bg, color: meta.color }}
                                                    >
                                                        {meta.label}
                                                    </span>
                                                    <span
                                                        className="text-[9px] font-bold px-1.5 py-0.5 rounded-md flex-shrink-0"
                                                        style={{ background: 'var(--v2-bg-surface)', color: 'var(--v2-text-muted)', border: '1px solid var(--v2-border-subtle)' }}
                                                    >
                                                        v{idx + 1}
                                                    </span>
                                                    <span className="text-[10px] text-[var(--v2-text-muted)] truncate ml-auto">{rDate}</span>
                                                </div>

                                                {/* Filename */}
                                                <p className="text-[12px] font-semibold text-[var(--v2-text-primary)] m-0 truncate leading-snug mb-2">
                                                    {rName || (rId ? `Report ${rId.slice(0, 14)}…` : 'Pathology Report')}
                                                </p>

                                                {/* Action buttons */}
                                                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                                    {rUrl && (
                                                        <button
                                                            type="button"
                                                            onClick={() => isSel ? openReportFile() : openByUrl(rUrl)}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold cursor-pointer transition-all border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] bg-[var(--v2-bg-surface)] hover:text-[var(--v2-primary-600)] hover:border-[var(--v2-primary-300)]"
                                                        >
                                                            <ExternalLink className="w-3 h-3" strokeWidth={2} /> Open
                                                        </button>
                                                    )}
                                                    {canUploadReport && (
                                                        <button
                                                            type="button"
                                                            onClick={() => { setSelectedReportId(rId); updateFileInputRef.current?.click(); }}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold cursor-pointer transition-all border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] bg-[var(--v2-bg-surface)] hover:text-[var(--v2-text-primary)]"
                                                        >
                                                            <FileUp className="w-3 h-3" strokeWidth={2} /> Replace
                                                        </button>
                                                    )}
                                                    {canDeleteReport && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setDeleteTarget({ id: rId, name: rName || `Report ${rId.slice(0, 14)}…` })}
                                                            className="inline-flex items-center justify-center w-7 h-7 rounded-lg cursor-pointer transition-all border border-transparent text-red-400 hover:text-red-600 hover:border-red-200 hover:bg-red-50 ml-auto"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* ══════════════════════════════════════════════
                            RIGHT PANEL — preview
                            ─ accent line only (no inner header bar)
                            ─ report metadata is the first element in
                              the padded body — part of content flow
                        ══════════════════════════════════════════════ */}
                        <div
                            className={[
                                'flex-1 min-w-0 rounded-[var(--v2-radius-lg)] border border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card)] overflow-hidden flex flex-col',
                                reports.length > 0
                                    ? (mobileTab === 'preview' ? 'flex flex-col' : 'hidden lg:flex lg:flex-col')
                                    : 'flex flex-col',
                            ].join(' ')}
                            style={{ minHeight: 400, boxShadow: 'var(--v2-shadow-card)' }}
                        >
                            {/* Accent line — the ONLY chrome on this panel */}
                            <div className="h-[3px]" style={{
                                background: 'linear-gradient(90deg,var(--v2-primary-500) 0%,var(--v2-primary-400) 50%,transparent 100%)',
                            }} />

                            {!report ? (
                                <div className="flex flex-col items-center justify-center flex-1 py-24 px-8 gap-5 text-center">
                                    <div
                                        className="w-14 h-14 rounded-2xl flex items-center justify-center"
                                        style={{ background: 'var(--v2-bg-surface)', border: '1.5px dashed var(--v2-border-card)' }}
                                    >
                                        <FileSearch className="w-6 h-6 text-[var(--v2-text-muted)]" strokeWidth={1.2} />
                                    </div>
                                    <div>
                                        <p className="text-[14px] font-bold text-[var(--v2-text-secondary)] m-0">No report selected</p>
                                        <p className="text-[12px] text-[var(--v2-text-muted)] m-0 mt-2 max-w-xs leading-relaxed">
                                            {reports.length
                                                ? 'Select a report from the list to preview it here.'
                                                : 'No reports uploaded for this study yet.'}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-4 sm:p-5 flex-1 flex flex-col min-h-0">
                                    <div className="flex items-center gap-2.5 mb-3">
                                        {/* File type badge */}
                                        {(() => {
                                            const m = getFileTypeMeta(report);
                                            return (
                                                <span
                                                    className="inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider flex-shrink-0"
                                                    style={{ background: m.bg, color: m.color }}
                                                >
                                                    {m.label}
                                                </span>
                                            );
                                        })()}

                                        {/* Filename */}
                                        <span className="text-[13px] font-bold text-[var(--v2-text-heading)] truncate flex-1 min-w-0">
                                            {reportFileName || (reportId ? `Report ${reportId.slice(0, 12)}…` : 'Pathology Report')}
                                        </span>

                                        {/* Date */}
                                        {report.date && (
                                            <span className="hidden sm:inline-flex items-center gap-1 text-[10px] text-[var(--v2-text-muted)] flex-shrink-0">
                                                <Calendar className="w-2.5 h-2.5" strokeWidth={2} />
                                                {fmtReportDate(report)}
                                            </span>
                                        )}

                                        {/* Open — ghost outline */}
                                        {reportUrl && (
                                            <button
                                                type="button"
                                                onClick={openReportFile}
                                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold cursor-pointer transition-all border flex-shrink-0 hover:opacity-80"
                                                style={{
                                                    background: 'transparent',
                                                    border: '1px solid var(--v2-border-brand,rgba(4,94,184,0.3))',
                                                    color: 'var(--v2-primary-600)',
                                                }}
                                            >
                                                <ExternalLink className="w-3 h-3" strokeWidth={2} />
                                                <span className="hidden sm:inline">Open</span>
                                            </button>
                                        )}
                                    </div>

                                    {/* Hairline separator between meta strip and preview */}
                                    <div className="mb-4 h-px bg-[var(--v2-border-subtle)]" />

                                    {/* Preview content */}
                                    {(reportPreviewLoading || reportLoading) ? (
                                        <div className="flex flex-col items-center justify-center flex-1 min-h-0 gap-3">
                                            <div className="w-full max-w-xs h-1 rounded-full bg-[var(--v2-bg-surface)] overflow-hidden">
                                                <div className="h-full rounded-full animate-shimmer" style={{ width: '60%', background: 'var(--v2-primary-400)' }} />
                                            </div>
                                            <p className="text-xs text-[var(--v2-text-muted)]">Loading preview…</p>
                                        </div>
                                    ) : (
                                        <ReportPreviewPanel
                                            preview={reportPreview}
                                            report={report}
                                            reportUrl={reportUrl}
                                            reportFileName={reportFileName}
                                            onOpen={openReportFile}
                                        />
                                    )}
                                </div>
                            )}
                        </div>

                    </div>
                </>
            )}
        </div>
    );
};

// ── Preview renderer ───────────────────────────────────────────────────────────

const previewBase = 'overflow-auto text-sm leading-relaxed border border-[var(--v2-border-card)]';

const ReportPreviewPanel: React.FC<{
    preview: ReportPreviewContent | null;
    report: PathologyReport;
    reportUrl: string;
    reportFileName: string;
    onOpen: () => void;
}> = ({ preview, report, reportUrl, reportFileName, onOpen }) => {
    const [htmlLoaded, setHtmlLoaded] = React.useState(false);
    const [pdfLoaded, setPdfLoaded] = React.useState(false);

    // Reset load state when preview changes
    React.useEffect(() => { setHtmlLoaded(false); setPdfLoaded(false); }, [preview]);

    if (preview?.kind === 'html' && preview.html?.trim()) {
        return (
            <div className="flex-1 min-h-0 relative rounded-xl overflow-hidden" style={{
                border: '1px solid var(--v2-border-card)',
                boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
            }}>
                {/* Shimmer overlay while HTML loads */}
                {!htmlLoaded && (
                    <div className="absolute inset-0 z-10 bg-[var(--v2-bg-surface)] flex flex-col items-center justify-center gap-3">
                        <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: 'var(--v2-primary-50,rgba(4,94,184,0.06))', border: '1.5px solid var(--v2-border-brand,rgba(4,94,184,0.15))' }}>
                            <FileText className="w-6 h-6" style={{ color: 'var(--v2-primary-500)' }} strokeWidth={1.5} />
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <p className="text-[13px] font-bold text-[var(--v2-text-secondary)] m-0">Rendering report…</p>
                            <div className="w-32 h-1 rounded-full bg-[var(--v2-bg-card)] overflow-hidden mt-1">
                                <div className="h-full rounded-full animate-shimmer w-2/3" style={{ background: 'var(--v2-primary-400)' }} />
                            </div>
                        </div>
                    </div>
                )}
                <iframe
                    title="Pathology report HTML"
                    srcDoc={preview.html}
                    className="w-full h-full"
                    style={{ background: '#fff', border: 'none' }}
                    onLoad={() => setHtmlLoaded(true)}
                />
            </div>
        );
    }
    if (preview?.kind === 'docx' && preview.html?.trim()) {
        return (
            <div
                className={`${previewBase} px-6 py-5 pathology-report-docx-preview flex-1 min-h-0 rounded-xl bg-[var(--v2-bg-surface)]`}
                style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}
                dangerouslySetInnerHTML={{ __html: preview.html }}
            />
        );
    }
    if (preview?.kind === 'pdf' && preview.blobUrl) {
        return (
            <div className="flex-1 min-h-0 relative rounded-xl overflow-hidden" style={{
                border: '1px solid var(--v2-border-card)',
                background: '#1a1a1a',
                boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
            }}>
                {/* Shimmer overlay while PDF loads */}
                {!pdfLoaded && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3" style={{ background: '#1a1a1a' }}>
                        <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.07)', border: '1.5px solid rgba(255,255,255,0.12)' }}>
                            <FileText className="w-6 h-6" style={{ color: 'rgba(255,255,255,0.5)' }} strokeWidth={1.5} />
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <p className="text-[13px] font-bold m-0" style={{ color: 'rgba(255,255,255,0.6)' }}>Loading PDF…</p>
                            <div className="w-32 h-1 rounded-full overflow-hidden mt-1" style={{ background: 'rgba(255,255,255,0.1)' }}>
                                <div className="h-full rounded-full animate-shimmer w-2/3" style={{ background: 'var(--v2-primary-400)' }} />
                            </div>
                        </div>
                    </div>
                )}
                <iframe
                    title="Pathology report PDF"
                    src={preview.blobUrl}
                    className="w-full h-full"
                    style={{ border: 'none' }}
                    onLoad={() => setPdfLoaded(true)}
                />
            </div>
        );
    }
    if (preview?.kind === 'text' && preview.text?.trim()) {
        return (
            <pre
                className={`${previewBase} px-6 py-5 m-0 whitespace-pre-wrap font-mono text-[var(--v2-text-secondary)] flex-1 min-h-0 rounded-xl bg-[var(--v2-bg-surface)]`}
                style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}
            >
                {preview.text}
            </pre>
        );
    }

    const hasStructured =
        Boolean(report.findings?.trim()) ||
        Boolean(report.conclusion?.trim()) ||
        Boolean(report.patientInfo ?? report.patient_info);

    if (hasStructured) {
        return (
            <div className="flex flex-col gap-4 overflow-y-auto flex-1 min-h-0 v2-scrollbar pr-1">
                <ReportSection title="Patient Information" content={formatPatientInfo(report.patientInfo ?? report.patient_info)} />
                <ReportSection title="Findings" content={report.findings} />
                <ReportSection title="Conclusion" content={report.conclusion} />
            </div>
        );
    }

    if (reportUrl) {
        return (
            <div className="flex flex-col items-center justify-center flex-1 min-h-0 gap-5 text-center">
                {/* Glassy unsupported-format card */}
                <div
                    className="flex flex-col items-center gap-4 px-8 py-10 rounded-2xl"
                    style={{
                        background: 'var(--v2-bg-surface)',
                        border: '1.5px dashed var(--v2-border-card)',
                        boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                    }}
                >
                    <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center"
                        style={{ background: 'var(--v2-primary-50,rgba(4,94,184,0.06))', border: '1.5px solid var(--v2-border-brand,rgba(4,94,184,0.15))' }}
                    >
                        <FileText className="w-6 h-6" style={{ color: 'var(--v2-primary-500)' }} strokeWidth={1.5} />
                    </div>
                    <div>
                        <p className="text-[14px] font-bold text-[var(--v2-text-secondary)] m-0">Report available</p>
                        <p className="text-[12px] text-[var(--v2-text-muted)] m-0 mt-2 max-w-[220px] leading-relaxed">
                            {preview?.kind === 'unsupported'
                                ? 'This file format cannot be previewed inline.'
                                : 'Preview is not available for this file format.'}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onOpen}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-[12px] font-bold cursor-pointer transition-all"
                        style={{
                            background: 'var(--v2-primary-600)',
                            border: 'none',
                            color: '#fff',
                            boxShadow: '0 2px 12px rgba(4,94,184,0.28)',
                        }}
                    >
                        <ExternalLink className="w-3.5 h-3.5" strokeWidth={2} />
                        {reportFileName ? `Open ${reportFileName}` : 'Open report file'}
                    </button>
                </div>
            </div>
        );
    }

    return null;
};

// ── Section block ─────────────────────────────────────────────────────────────

const ReportSection: React.FC<{ title: string; content?: string }> = ({ title, content }) => (
    <div className="flex flex-col gap-1.5">
        <h3 className="text-[9px] font-black uppercase tracking-widest text-[var(--v2-text-muted)] m-0">{title}</h3>
        <div className="rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-surface)] px-4 py-3 text-[13px] leading-relaxed whitespace-pre-wrap text-[var(--v2-text-secondary)] min-h-12">
            {content?.trim() ? content : '—'}
        </div>
    </div>
);

export default Reports;
